import React from "react";
import _ from "lodash";
import moment from "moment-timezone";
import Highcharts from "highcharts";
import numeral from "numeral";
import { UnitAPI, NetworkAPI } from "apis";

import HighLightRowWrapper from "components/common/HighLightRowWrapper";
import ClickToCopyWrapper from "components/common/ClickToCopyWrapper";
import FirstLookPriceSection from "components/network/pricing-rules/first-look-rules/FirstLookPriceSection";
import UprTargetingPopover from "components/ops-mgmt/account/custom-upr/UprTargetingPopover";
import PropsPopover from "components/common/PropsPopover";
import I2WLabel from "components/common/I2WLabel";
import SortButton from "components/common/SortButton";
import AppliedBaseModal from "./AppliedBaseModal";

import {
  getAppliedGeneralChartOptions,
  getPublisherFLSubChartOptions,
} from "./AnatomyCstChartHelper";
import {
  APPLIED_CHART_TYPES,
  GROUP_TYPE,
  TAB,
  thClass,
} from "./AppliedDataHelper";
import { getCurrencySymbol } from "helpers/Utils";
import { tdClass } from "helpers/StyleClass";
import { setQueryString } from "helpers/QueryString";
import { getDateList } from "helpers/DateTransform";
import { normalizeRevenue } from "helpers/Chart";
import { SORT_DIRECTIONS } from "constants/UI";
import { transformRules } from "components/network/pricing-rules/first-look-rules/FirstLookHelper";
import { FiFileText, FiSearch } from "react-icons/fi";

class PublisherFirstLookModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      popupId: "",
      errMsg: null,
      hoverIndex: "",
      startDate: "",
      endDate: "",
      apiParam: {},
      dataParam: {},

      typeList: [],
      dateList: [],
      originData: null,
      transformedRules: [],
      groupData: {},
      tableData: [],
      lineChartDataOptions: {},
      subChartDataOptions: {},

      selectedTab: null,
      tabOptions: [],

      mainChartSelected: null,
      mainChartOptions: [],

      subChartSelected: null,
      subChartOptions: [],

      selectedItems: [],
      networkInfo: null,
      units: null,
      virtualPlacements: null,
      protectedFLIds: null,
      preservedFLIds: null,
      resourceById: null,

      sortKey: "",
      sortOrder: SORT_DIRECTIONS.DESC,
    };

    this.handleChartSelected = this.handleChartSelected.bind(this);
    this.handleChartReset = this.handleChartReset.bind(this);
    this.handleGetData = this.handleGetData.bind(this);
    this.handleSelectItem = this.handleSelectItem.bind(this);
    this.handleSelectItemAll = this.handleSelectItemAll.bind(this);
    this.handleImportSelected = this.handleImportSelected.bind(this);

    this.parseDataFormat = this.parseDataFormat.bind(this);
  }

  async componentDidMount() {
    const {
      startDate,
      endDate = moment().format("YYYY-MM-DD"),
      includeToday = true,
      networkId,
    } = this.props;

    this.setState({ isLoading: true, errMsg: null });
    try {
      const [
        networkInfo,
        units,
        virtualPlacements,
        { config },
        { first_look = {} },
      ] = await Promise.all([
        NetworkAPI.getNetworkInfo({
          networkId,
        }),
        NetworkAPI.getUnitsByNetworkId({ networkId }),
        NetworkAPI.getNetworkVirtualPlacements({ networkId }),
        NetworkAPI.getRawNetworkFeatures({ networkId }),
        NetworkAPI.getNetworkResourceMapping({ networkId }),
      ]);

      const protectedFLIds = _.get(config, "publisherFLProtectionIds", []);
      const preservedFLIds = _.get(config, "preservedFLIds", []);

      const resourceById = _.keyBy(first_look.mapping, (item) => {
        return item.id;
      });

      const parsedEndDate = includeToday
        ? endDate.split(" ")[0]
        : moment(endDate).subtract(1, "day").format("YYYY-MM-DD");

      await this.handleGetData({
        startDate: startDate.split(" ")[0],
        endDate: parsedEndDate,
        protectedFLIds,
        preservedFLIds,
        resourceById,
        networkInfo,
        units,
        virtualPlacements,
        dataParam: {
          startDate: startDate.split(" ")[0],
          endDate: parsedEndDate,
        },
      });
    } catch (error) {
      this.setState({ errMsg: error.message || "Failed to fetch data" });
    } finally {
      this.setState({ isLoading: false });
    }
  }

  async handleChartSelected({ min, max, pos }) {
    let selectedDates = [];
    const { dateList } = this.state;

    if (!_.isNil(min) && !_.isNil(max)) {
      const startIndex = Math.ceil(min);
      const endIndex = Math.floor(max);
      selectedDates = dateList.slice(startIndex, endIndex + 1);
    } else if (!_.isNil(pos)) {
      selectedDates = [dateList[pos], dateList[pos]];
    }

    if (selectedDates.length === 0) return;
    this.setState({ isLoading: true });

    try {
      await this.handleGetData({
        startDate: selectedDates[0],
        endDate: selectedDates[selectedDates.length - 1],
      });
    } catch (error) {
      this.setState({ errMsg: error.message || "Failed to fetch data" });
    } finally {
      this.setState({ isLoading: false });
    }
  }

  handleChartReset() {
    const { dataParam } = this.state;
    this.handleGetData({ ...dataParam });
  }

  async handleGetData({
    startDate = this.state.startDate,
    endDate = this.state.endDate,
    protectedFLIds = this.state.protectedFLIds,
    preservedFLIds = this.state.preservedFLIds,
    resourceById = this.state.resourceById,
    networkInfo = this.state.networkInfo,
    units = this.state.units,
    virtualPlacements = this.state.virtualPlacements,
    dataParam,
  }) {
    const { unitId } = this.props;
    const apiStartDate = _.get(this.state, "apiParam.startDate");
    const isFetchData =
      _.isNil(apiStartDate) || moment(startDate).isBefore(apiStartDate);

    let reports = [];
    let transformedRules = [];

    if (isFetchData) {
      const result = await UnitAPI.getBenchmarkFirstRuleReport({
        unitId,
        startDate,
      });

      reports = result.reports || [];

      transformedRules = transformRules({
        rules: _.values(result.props || {}),
        protectedFLIds,
        preservedFLIds,
        resourceById,
      });
    } else {
      reports = this.state.originData || [];
      transformedRules = this.state.transformedRules || [];
    }

    // Filter in2w rules and normalize revenue
    const filteredIn2wRule = _.filter(transformedRules, (rule) =>
      rule.name.includes("in2w_")
    ).map((rule) => rule.externalId);

    const dateList = getDateList(startDate, endDate, true);

    const filteredReports = _.filter(
      reports,
      (report) =>
        !filteredIn2wRule.includes(report.externalId) &&
        dateList.includes(report.date)
    ).map((item) => ({
      ...item,
      revenue: normalizeRevenue(item.revenue),
    }));

    const groupByType = _.groupBy(filteredReports, (item) => item.group_type);
    const typeList = _.keys(groupByType);

    const { tabOptions, mainChartOptions, subChartOptions } =
      this._getAllOptions({ typeList });

    const metaData = this._parseMetaData({
      data: groupByType,
      dateList,
    });

    const groupData = this._getDataByDate({
      data: metaData,
    });

    const tableData = this._getDataByExternalId({
      data: metaData,
      typeList,
      transformedRules,
    });

    const isDateRangeChanged = !_.isEqual(dataParam || this.state.dataParam, {
      startDate,
      endDate,
    });

    const lineChartDataOptions = {};
    const subChartDataOptions = {};
    _.forEach(tabOptions, (tab) => {
      _.forEach(mainChartOptions, (chart, idx) => {
        _.set(
          lineChartDataOptions,
          [tab.value, chart.value],
          getAppliedGeneralChartOptions({
            xAxis: dateList,
            dataList: tab.groupList.map((type) => ({
              name: `${type}`,
              data: _.get(groupData, [type, chart.value], []),
            })),
            chartType: chart.value,
            currency: networkInfo.currency,
            selectFn: this.handleChartSelected,
            resetFn: isDateRangeChanged ? this.handleChartReset : null,
          })
        );

        _.set(
          subChartDataOptions,
          [tab.value, chart.value],
          tab.groupList.map((type) =>
            getPublisherFLSubChartOptions({
              xAxis: dateList,
              dataList: _.filter(tableData, (item) => item.data[type]).map(
                (item) => ({
                  name: item.externalId,
                  data: _.get(item, ["data", type, chart.value, "list"], []),
                  color: item.color,
                })
              ),
              chartType: chart.value,
              currency: networkInfo.currency,
              title: tab.groupList.length > 1 ? type : "",
            })
          )
        );
      });
    });

    const stateData = {};

    if (isFetchData) {
      stateData.apiParam = {
        startDate,
        endDate,
      };
    }

    if (!_.isNil(dataParam)) {
      stateData.dataParam = dataParam;
    }

    const selectedTabIdx = _.findIndex(
      tabOptions,
      (option) => option.value === this.state.selectedTab
    );
    if (selectedTabIdx < 0 && tabOptions.length > 0) {
      stateData.selectedTab = _.get(tabOptions, "[0].value", null);
    }

    const sortIdx = selectedTabIdx < 0 ? 0 : selectedTabIdx;
    stateData.sortKey = `${_.get(tabOptions, [sortIdx, "groupList", 0], "")}.${
      this.state.sortKey.split(".")[1] || APPLIED_CHART_TYPES.IMPRESSION
    }`;

    const selectedMainIdx = _.findIndex(
      mainChartOptions,
      (option) => option.value === this.state.mainChartSelected
    );
    if (selectedMainIdx < 0 && mainChartOptions.length > 0) {
      stateData.mainChartSelected = _.get(mainChartOptions, "[0].value", null);
    }

    const selectedSubIdx = _.findIndex(
      subChartOptions,
      (option) => option.value === this.state.subChartSelected
    );
    if (selectedSubIdx < 0 && subChartOptions.length > 0) {
      stateData.subChartSelected = _.get(subChartOptions, "[0].value", null);
    }

    this.setState({
      ...stateData,
      startDate,
      endDate,

      groupData,
      tableData,
      lineChartDataOptions,
      subChartDataOptions,

      tabOptions,

      mainChartOptions,

      subChartOptions,

      dateList,
      typeList,
      originData: reports,
      transformedRules,

      networkInfo,
      units,
      virtualPlacements,
      protectedFLIds,
      preservedFLIds,
      resourceById,

      selectedItems: [],
    });
  }

  handleSelectItem(id) {
    const { selectedItems } = this.state;

    this.setState({
      selectedItems:
        _.indexOf(selectedItems, id) >= 0
          ? _.filter(selectedItems, (item) => item !== id)
          : [...selectedItems, id],
    });
  }

  handleSelectItemAll(list) {
    this.setState({
      selectedItems: list,
    });
  }

  handleImportSelected() {
    const { selectedItems, networkInfo } = this.state;
    const queryString = setQueryString(
      this.props,
      { importId: selectedItems },
      true
    );
    window.open(
      `${window.location.origin}/#/ops-mgmt/account/network/${networkInfo.networkId}/first-look-setting?${queryString}`
    );
  }

  parseDataFormat(data, type) {
    const { networkInfo } = this.state;
    let result = "";
    if (type === APPLIED_CHART_TYPES.REVENUE) {
      result =
        getCurrencySymbol(networkInfo.currency) +
        numeral(data).format("0,0.00");
    } else if (type === APPLIED_CHART_TYPES.IMPRESSION) {
      result = numeral(data).format("0,0");
    } else if (type === APPLIED_CHART_TYPES.ECPM) {
      result =
        getCurrencySymbol(networkInfo.currency) +
        numeral(data).format("0,0.00");
    }
    return result;
  }

  // { [GROUP_TYPE]: { externalId: { [APPLIED_CHART_TYPES]: number[] }} }
  // ex. b: { 1111: { revenue: [], impression: [] }}
  _parseMetaData({ data, dateList }) {
    return _.mapValues(data, (list) => {
      const groupByExternalId = _.groupBy(list, (item) => item.external_id);
      return _.mapValues(groupByExternalId, (items) => {
        const temp = [
          APPLIED_CHART_TYPES.IMPRESSION,
          APPLIED_CHART_TYPES.REVENUE,
        ].map((d) => [d, d]);
        return _.mapValues(_.fromPairs(temp), (type) => {
          return _.map(dateList, (date) => {
            const item = _.find(
              items,
              (item) => item[type] && item.date === date
            );
            return item ? item[type] : 0;
          });
        });
      });
    });
  }

  // { [GROUP_TYPE]: { [APPLIED_CHART_TYPES]: number[] }}
  // ex. b: { revenue: [], impression: [] }
  _getDataByDate({ data }) {
    return _.mapValues(data, (dataObj) => {
      const temp = {};
      _.forEach(
        [APPLIED_CHART_TYPES.REVENUE, APPLIED_CHART_TYPES.IMPRESSION],
        (dataType) => {
          _.set(
            temp,
            [dataType],
            _.zipWith(
              ..._.values(dataObj).map((obj) => obj[dataType]),
              (...nums) => _.sum(nums)
            )
          );
        }
      );

      _.set(
        temp,
        [APPLIED_CHART_TYPES.ECPM],
        _.map(temp[APPLIED_CHART_TYPES.IMPRESSION], (impression, index) => {
          return impression === 0
            ? 0
            : (_.get(temp[APPLIED_CHART_TYPES.REVENUE], index, 0) /
                impression) *
                1000;
        })
      );

      return temp;
    });
  }

  // { ...rule, color , data: { [GROUP_TYPE]: { [APPLIED_CHART_TYPES]: { amount, list } }}}[]
  // ex. { ...rule, color, data: { b: { revenue: { amount: 0, list: [] }, ... } } }}
  _getDataByExternalId({ data, typeList, transformedRules }) {
    const result = [];
    const externalIdList = _.uniq(
      _.flattenDeep(_.keys(data).map((key) => _.keys(data[key])))
    );

    externalIdList.forEach((exId) => {
      const temp = {};
      _.forEach(typeList, (type) => {
        if (!data[type][exId]) return;
        const revenue = _.get(
          data,
          [type, exId, APPLIED_CHART_TYPES.REVENUE],
          []
        );
        const impression = _.get(
          data,
          [type, exId, APPLIED_CHART_TYPES.IMPRESSION],
          []
        );

        const revenueSum = _.sum(revenue);
        const impressionSum = _.sum(impression);

        temp[type] = {
          revenue: {
            amount: revenueSum,
            list: revenue,
          },
          impression: {
            amount: impressionSum,
            list: impression,
          },
          ecpm: {
            amount:
              impressionSum === 0 ? 0 : (revenueSum / impressionSum) * 1000,
            list: _.map(impression, (imp, idx) => {
              return imp === 0 ? 0 : (revenue[idx] / imp) * 1000;
            }),
          },
        };
      });

      result.push({
        externalId: exId,
        data: temp,
      });
    });

    const parsedTableData = result.map((item) => ({
      ...item,
      ...(_.find(transformedRules, {
        externalId: item.externalId,
      }) || {}),
    }));

    const sortedTableData = _.orderBy(
      parsedTableData,
      ["priority"],
      [SORT_DIRECTIONS.ASC]
    ).map((item, idx) => ({
      ...item,
      color: Highcharts.getOptions().colors[idx % 10],
    }));

    return sortedTableData;
  }

  _getAllOptions({ typeList }) {
    const tabOptions = [];
    if (typeList.includes(GROUP_TYPE.B) || typeList.includes(GROUP_TYPE.O)) {
      tabOptions.push({
        value: TAB.BO,
        label: "b vs o",
        groupList: [GROUP_TYPE.B, GROUP_TYPE.O],
      });
    }

    if (typeList.includes(GROUP_TYPE.UM)) {
      tabOptions.push({
        value: TAB.UM,
        label: "um",
        groupList: [GROUP_TYPE.UM],
      });
    }

    if (typeList.includes(GROUP_TYPE.UNKNOWN)) {
      tabOptions.push({
        value: TAB.UNKNOWN,
        label: "unknown",
        groupList: [GROUP_TYPE.UNKNOWN],
      });
    }

    const chartOptions = _.values(APPLIED_CHART_TYPES).map((data) => ({
      value: data,
      label: data,
    }));

    return {
      tabOptions,
      mainChartOptions: _.cloneDeep(chartOptions),
      subChartOptions: _.cloneDeep(chartOptions),
    };
  }

  render() {
    const { unitId, unitName, onClose } = this.props;

    const {
      isLoading,
      popupId,
      hoverIndex,
      dataParam,
      errMsg,

      tabOptions,
      selectedTab,
      mainChartOptions,
      subChartOptions,
      mainChartSelected,
      subChartSelected,

      tableData,
      lineChartDataOptions,
      subChartDataOptions,

      selectedItems,
      networkInfo,
      units,
      virtualPlacements,

      sortKey,
      sortOrder,
    } = this.state;

    const parseTab = selectedTab
      ? _.find(tabOptions, { value: selectedTab })
      : {};

    const filterData = _.filter(tableData, ({ data }) =>
      _.some(parseTab.groupList, (groupType) => data[groupType])
    );

    const isSelectAll =
      filterData.length > 0 && selectedItems.length === filterData.length;

    const parsedSortKey = sortKey === "" ? sortKey : `data.${sortKey}.amount`;

    const sortTableData = _.orderBy(
      filterData,
      parsedSortKey !== "" ? [(d) => _.get(d, parsedSortKey, 0)] : [],
      [sortOrder]
    );

    return (
      <AppliedBaseModal
        onClose={onClose}
        title={{
          label: "Applied First Look ",
          highlight:
            dataParam && dataParam.startDate && dataParam.endDate
              ? `${unitId || ""} - ${unitName || ""} [${
                  dataParam.startDate
                } ~ ${dataParam.endDate}]`
              : "",
        }}
        isLoading={isLoading}
        errMsg={errMsg}
        isEmptyData={_.isEmpty(tableData)}
        tabOptions={tabOptions}
        selectedTab={selectedTab}
        mainChartOptions={mainChartOptions}
        mainChartSelected={mainChartSelected}
        subChartOptions={subChartOptions}
        subChartSelected={subChartSelected}
        mainChartData={lineChartDataOptions}
        subChartData={subChartDataOptions}
        filterData={filterData}
        selectedItems={selectedItems}
        handleTabChanged={(option) =>
          this.setState({
            selectedTab: option.value,
            sortKey:
              sortKey !== ""
                ? `${option.groupList[0]}.${
                    sortKey.split(".")[1] || APPLIED_CHART_TYPES.IMPRESSION
                  }`
                : "",
          })
        }
        handleMainChartChanged={(mainChartSelected) =>
          this.setState({ mainChartSelected })
        }
        handleSubChartChanged={(subChartSelected) =>
          this.setState({ subChartSelected })
        }
        handleImportSelected={this.handleImportSelected}
      >
        {!_.isEmpty(parseTab) && (
          <table className="table w-full text-sm">
            <thead className="border-b bg-gray-200 text-xs text-blue-800">
              <tr>
                <th className={thClass + " text-center"} colSpan={4}></th>
                {parseTab.groupList.map((groupType) => (
                  <th
                    className={`${
                      hoverIndex === groupType ? "bg-yellow-200" : ""
                    } ${thClass} text-center`}
                    key={groupType}
                    colSpan={3}
                    onMouseEnter={() =>
                      this.setState({ hoverIndex: groupType })
                    }
                    onMouseLeave={() => this.setState({ hoverIndex: "" })}
                  >
                    {groupType}
                  </th>
                ))}
              </tr>
              <tr>
                <th className={thClass + " w-12 text-center"}>
                  <input
                    style={{
                      width: "18px",
                      height: "18px",
                      marginTop: "4px",
                    }}
                    type="checkbox"
                    checked={isSelectAll}
                    onChange={() =>
                      this.handleSelectItemAll(
                        isSelectAll
                          ? []
                          : filterData.map((item) => item.externalId)
                      )
                    }
                  ></input>
                </th>
                <th className={thClass + " text-center"}>External ID</th>
                <th className={thClass + " text-right"}>Price Section</th>
                <th className={thClass + " w-12"}></th>
                {parseTab.groupList.map((gType) => (
                  <React.Fragment key={gType}>
                    {_.values(APPLIED_CHART_TYPES).map((dataType) => (
                      <th
                        className={`${
                          hoverIndex === gType ? "bg-yellow-200" : ""
                        } ${thClass} text-right`}
                        key={`th-${gType}-${dataType}`}
                      >
                        <div className="flex items-center">
                          {dataType}
                          <SortButton
                            sortKey={`${gType}.${dataType}`}
                            currentSortKey={sortKey}
                            currentSortOrder={sortOrder}
                            onClick={({ sortKey = "", sortOrder = "" }) =>
                              this.setState({ sortKey, sortOrder })
                            }
                          />
                        </div>
                      </th>
                    ))}
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white  text-gray-900">
              {sortTableData.map((item) => {
                const gamLink = `https://admanager.google.com/${networkInfo.code}#inventory/first_look/detail/first_look_id=${item.externalId}`;

                const parsedName = item.__isI2wRule
                  ? `${item.props && item.props.name ? item.props.name : ""}`
                  : item.name;
                return (
                  <HighLightRowWrapper
                    selected={popupId === item.externalId}
                    key={item.externalId}
                  >
                    <td className={tdClass + " text-center"}>
                      <input
                        style={{
                          width: "18px",
                          height: "18px",
                          marginTop: "4px",
                        }}
                        type="checkbox"
                        checked={
                          _.indexOf(selectedItems, item.externalId) !== -1
                        }
                        onChange={() => this.handleSelectItem(item.externalId)}
                      ></input>
                    </td>
                    <td className={tdClass + ""}>
                      <div className="flex items-center gap-1">
                        <span
                          className={`font-mono ${
                            popupId === item.externalId ? "font-semibold" : ""
                          }`}
                        >
                          {item.externalId}
                        </span>

                        <div className="flex items-center text-blue-500">
                          <ClickToCopyWrapper
                            copyText={gamLink}
                            tooltipMessage="Click to copy GAM link"
                          ></ClickToCopyWrapper>
                        </div>
                      </div>
                      <I2WLabel item={item} isSmall={true}></I2WLabel>
                      <div className="flex items-center gap-1">
                        <div className="text-xs">{parsedName}</div>
                        {item.__isI2wRule && (
                          <PropsPopover
                            item={item}
                            place="right"
                            resourceType="first_look"
                            onOpen={() =>
                              this.setState({
                                popupId: item.externalId,
                              })
                            }
                            onClose={() => this.setState({ popupId: "" })}
                            triggerElement={
                              <div className="cursor-pointer  text-blue-600 hover:text-blue-700">
                                <FiSearch />
                              </div>
                            }
                            titleElement={
                              <>
                                Props: {item.externalId} - {parsedName}
                                <div className="text-sm text-gray-700">
                                  {item.name}
                                </div>
                              </>
                            }
                          ></PropsPopover>
                        )}
                      </div>
                    </td>
                    <td className={tdClass + " text-right"}>
                      {item.priceSection ? (
                        <FirstLookPriceSection
                          item={item.priceSection}
                          networkId={networkInfo.networkId}
                          onOpen={() =>
                            this.setState({
                              popupId: item.externalId,
                            })
                          }
                          onClose={() => this.setState({ popupId: "" })}
                        ></FirstLookPriceSection>
                      ) : (
                        "--"
                      )}
                    </td>
                    <td className={tdClass + " text-center"}>
                      <div>
                        {item.targetingSection && (
                          <UprTargetingPopover
                            place="left bottom"
                            id={item.externalId}
                            name={item.name}
                            networkId={networkInfo.networkId}
                            units={units}
                            virtualPlacements={virtualPlacements}
                            targetingSection={item.targetingSection}
                            triggerElement={
                              <div className="cursor-pointer  text-blue-600 hover:text-blue-700">
                                <FiFileText />
                              </div>
                            }
                            onOpen={() =>
                              this.setState({
                                popupId: item.externalId,
                              })
                            }
                            onClose={() => this.setState({ popupId: "" })}
                          ></UprTargetingPopover>
                        )}
                      </div>
                    </td>
                    {parseTab.groupList.map((gType) => (
                      <React.Fragment key={gType}>
                        {_.values(APPLIED_CHART_TYPES).map((dataType) => (
                          <td
                            className={`${tdClass} text-right font-mono ${
                              hoverIndex === gType ? "bg-yellow-100" : ""
                            }`}
                            key={`td-${gType}-${dataType}`}
                          >
                            {this.parseDataFormat(
                              _.get(
                                item,
                                ["data", gType, dataType, "amount"],
                                0
                              ),
                              dataType
                            )}
                          </td>
                        ))}
                      </React.Fragment>
                    ))}
                  </HighLightRowWrapper>
                );
              })}
            </tbody>
          </table>
        )}
      </AppliedBaseModal>
    );
  }
}

export default PublisherFirstLookModal;
